import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import type { ReactNode } from 'react'

interface IfGlobalOrgProps {
  children: ReactNode
  show?: boolean
  fallback?: ReactNode
}

/**
 * Examples:
 * (remember, show is true by default and fallback is null by default)
 * 1.
 * <IfGlobalOrg>
 *   <ContentForGlobalOrg />
 * </IfGlobalOrg>
 *
 * 2.
 * <IfGlobalOrg fallback={<ContentForNonGlobalOrg />}>
 *   <ContentForGlobalOrg />
 * </IfGlobalOrg>
 *
 * 3.
 * <IfGlobalOrg show={false}>
 *   <ContentToHideFromGlobalOrg />
 * </IfGlobalOrg>
 *
 * 4. (functionaly the same as 2)
 * <IfGlobalOrg show={false} fallback={<ContentForGlobalOrg />}>
 *   <ContentForNonGlobalOrg />
 * </IfGlobalOrg>
 */
const IfGlobalOrg = ({ children, show = true, fallback = null }: IfGlobalOrgProps) => {
  const isGlobalOrg = useIsGlobalOrg()

  // If global and "show", return the wrapped content
  // If global and not "show", return the fallback
  // If not global and "show", return  the fallback (probably null)
  // If not global and not "show", return the wrapped content (if we were
  //   trying to hide it from global orgs, then show it to non-global orgs)
  return isGlobalOrg === show ? children : fallback
}

export default IfGlobalOrg
